/**
 * Due Diligence Types
 *
 * Types for the configurable due diligence system that allows
 * administrators to define custom categories, questions, and
 * answer options with color-coded status indicators.
 */

// ============================================
// Enums
// ============================================

export enum DueDiligenceScope {
  BUILDING = 'building',
  UNIT = 'unit'
}

export enum DueDiligenceStatusColor {
  GREEN = 'green',
  ORANGE = 'orange',
  RED = 'red',
  TRANSPARENT = 'transparent'
}

export enum DueDiligenceAnswerType {
  /** Status question - shows colored status dot in overview */
  OPTIONS = 'options',
  /** Simple selection - NO status dot displayed, useful for helper/dependency questions */
  SIMPLE_SELECT = 'simple_select',
  FREE_TEXT = 'free_text',
  DATE = 'date',
  NUMBER = 'number',
  BOOLEAN = 'boolean',
  /** Automatically checks if a file with the linked label exists */
  DOCUMENT_EXISTS = 'document_exists',
  /** Extract value from document, NO status dot displayed */
  EXTRACTION = 'extraction',
}

/**
 * Value types for EXTRACTION questions
 */
export type ExtractionValueType = 'number' | 'string' | 'date' | 'boolean';

/**
 * Evaluation mode for a due diligence question
 */
export enum DueDiligenceEvaluationMode {
  /** Traditional manual response selection via answer options */
  MANUAL = 'manual',
  /** Automatic evaluation based on FileLog data and configured rules */
  FILE_BASED = 'file_based',
}

/**
 * Tracks how a DD response was entered - for audit trail
 */
export enum DueDiligenceResponseSourceType {
  /** User manually entered via DD modal */
  MANUAL = 'manual',
  /** AI extracted from document in dropzone */
  AI_EXTRACTED = 'ai_extracted',
  /** AI extracted but user validated/confirmed the answer */
  AI_VALIDATED = 'ai_validated',
  /** AI extracted but user edited/changed the answer */
  AI_EDITED = 'ai_edited',
  /** System automatically re-evaluated based on FileLog data changes */
  SYSTEM_REEVALUATION = 'system_reevaluation',
}

/**
 * Types of rules that can be evaluated
 */
export enum StatusRuleType {
  /** Check if file with the linked label exists */
  FILE_EXISTS = 'file_exists',
  /** Check expiration date with configurable thresholds */
  EXPIRATION_CHECK = 'expiration_check',
  /** Check document date (age of document) */
  DOCUMENT_DATE_CHECK = 'document_date_check',
  /** Check FileLog validity enum (geldig, niet_geldig, onbekend) */
  VALIDITY_CHECK = 'validity_check',
  /** Check FileLog conformity enum */
  CONFORMITY_CHECK = 'conformity_check',
  /** Check FileLog status enum (Ok, Ok with remarks, etc.) */
  STATUS_CHECK = 'status_check',
  /** Check a specific field value */
  FIELD_VALUE = 'field_value',
  /** Check extractedData JSON field */
  EXTRACTED_DATA = 'extracted_data',
}

/**
 * Operators for field comparisons
 */
export enum StatusRuleOperator {
  EQUALS = '==',
  NOT_EQUALS = '!=',
  GREATER_THAN = '>',
  LESS_THAN = '<',
  GREATER_THAN_OR_EQUAL = '>=',
  LESS_THAN_OR_EQUAL = '<=',
  CONTAINS = 'contains',
  NOT_CONTAINS = 'not_contains',
  NOT_EMPTY = 'not_empty',
  IS_EMPTY = 'is_empty',
  IN = 'in',
  NOT_IN = 'not_in',
}

// ============================================
// Status Rule Types (for file-based evaluation)
// ============================================

/**
 * Threshold configuration for expiration-based status determination
 */
export interface ExpirationThresholds {
  /**
   * Days before expiry to show orange warning.
   * If current date + orangeWarningDays >= expirationDate, show orange.
   * Example: 30 means "warn 30 days before expiry"
   */
  orangeWarningDays: number;

  /**
   * Optional: Days before expiry where it's still considered "good" green.
   * If not set, any non-expired date beyond orangeWarningDays is green.
   */
  greenValidDays?: number;
}

/**
 * Configuration for document date (age) checks
 */
export interface DocumentDateThresholds {
  /**
   * Maximum age in days for green status.
   * If document is older than this, it's orange or red.
   */
  maxAgeDaysGreen: number;

  /**
   * Maximum age in days for orange status.
   * If document is older than this, it's red.
   */
  maxAgeDaysOrange?: number;
}

/**
 * Single status rule definition
 */
export interface StatusRule {
  /** Type of rule to evaluate */
  type: StatusRuleType;

  /**
   * Field path for field_value/extracted_data types.
   * Supports dot notation for nested fields: "extractedData.policy.number"
   */
  field?: string;

  /** Comparison operator for field comparisons */
  operator?: StatusRuleOperator;

  /** Expected value for comparison */
  value?: any;

  /** Thresholds for expiration_check type */
  expirationThresholds?: ExpirationThresholds;

  /** Thresholds for document_date_check type */
  documentDateThresholds?: DocumentDateThresholds;

  /**
   * Status color when this rule matches/passes.
   * For expiration rules, this is the "good" (green) status.
   */
  resultStatusColor: DueDiligenceStatusColor;

  /**
   * Template for tooltip text. Supports placeholders:
   * - {field}: The field value
   * - {date}: Formatted date
   * - {days}: Number of days
   */
  tooltipTemplate?: string;
}

/**
 * Logical operator for combining rules
 */
export type LogicalOperator = 'AND' | 'OR';

/**
 * Group of rules with logical operator
 * Supports nested groups for complex logic
 */
export interface StatusRuleGroup {
  /** How to combine the rules in this group */
  logicalOperator: LogicalOperator;

  /** Rules or nested groups to evaluate */
  rules: (StatusRule | StatusRuleGroup)[];

  /** Override status color if entire group matches */
  resultStatusColor?: DueDiligenceStatusColor;

  /** Override tooltip if entire group matches */
  tooltipTemplate?: string;
}

/**
 * Strategy for handling multiple files with the same label
 */
export type AggregationStrategy =
  | 'latest'   // Use most recently updated file
  | 'worst'    // Use file with worst (most red) status
  | 'best'     // Use file with best (most green) status
  | 'any';     // Any file matching is enough (for file_exists)

/**
 * Complete configuration for automatic status evaluation
 */
export interface StatusEvaluationConfig {
  /** Rules to evaluate - first matching determines status */
  statusRules?: StatusRuleGroup;

  /** Default status when no rules match or no file exists */
  defaultStatus: DueDiligenceStatusColor;

  /** Default tooltip when no rules match or no file exists */
  defaultTooltip: string;

  /** How to handle multiple files with matching label */
  aggregationStrategy: AggregationStrategy;
}

/**
 * Type guard to check if an item is a StatusRule
 */
export function isStatusRule(item: StatusRule | StatusRuleGroup): item is StatusRule {
  return 'type' in item && Object.values(StatusRuleType).includes(item.type as StatusRuleType);
}

/**
 * Type guard to check if an item is a StatusRuleGroup
 */
export function isStatusRuleGroup(item: StatusRule | StatusRuleGroup): item is StatusRuleGroup {
  return 'logicalOperator' in item && 'rules' in item;
}

// ============================================
// Answer Option Conditions (NEW - per-option conditions)
// ============================================

/**
 * Types of conditions that can be configured per answer option
 */
export enum AnswerOptionConditionType {
  /** Check if file with linked label exists */
  FILE_EXISTS = 'file_exists',
  /** Check expiration date with configurable thresholds */
  EXPIRATION_DATE = 'expiration_date',
  /** Check a specific FileLog field value */
  FIELD_VALUE = 'field_value',
  /** Ask AI a yes/no question about the document */
  AI_QUESTION = 'ai_question',
  /** AI looks for a specific reference/value in the document */
  AI_REFERENCE = 'ai_reference',
  /** Compare a value extracted from document(s) against a database field */
  DATABASE_COMPARISON = 'database_comparison',
  /** Check the answer of another due diligence question */
  QUESTION_ANSWER = 'question_answer',
}

/**
 * Date unit for flexible date conditions
 */
export type DateUnit = 'days' | 'months' | 'years';

/**
 * AI location hints - where to look in the document
 */
export interface AILocationHints {
  /** Near specific pages, e.g., "last pages", "page 10" */
  nearPages?: string;
  /** Near specific keywords */
  nearKeywords?: string[];
  /** Near a specific title/section */
  nearTitle?: string;
}

/**
 * Expiration date comparison types
 * Each answer option defines when IT should match based on expiration
 */
export type ExpirationComparison =
  | 'more_than_days'      // Expiration is MORE than X days away (green scenario)
  | 'within_days'         // Expiration is WITHIN X days (orange/warning scenario)
  | 'expired'             // Document is already expired (red scenario)
  | 'not_expired';        // Document is not expired (any valid date)

/**
 * A condition group that can contain conditions or nested groups
 * This allows complex logic like: (A AND B) OR (C AND D)
 */
export interface ConditionGroup {
  /** Unique ID for React keys */
  id: string;
  /** Marks this as a group */
  type: 'group';
  /** How to combine children: AND = all must pass, OR = any can pass */
  logic: 'AND' | 'OR';
  /** Children can be conditions or nested groups */
  children: ConditionNode[];
}

/**
 * A condition node can be either a single condition or a group of conditions
 */
export type ConditionNode = AnswerOptionCondition | ConditionGroup;

/**
 * Type guard to check if a node is a group
 */
export function isConditionGroup(node: ConditionNode): node is ConditionGroup {
  return node.type === 'group';
}

/**
 * A single condition for an answer option
 * The answer option is selected if ALL its conditions are met (AND) or ANY condition is met (OR)
 */
export interface AnswerOptionCondition {
  /** Unique ID for React keys */
  id: string;
  /** Type of condition */
  type: AnswerOptionConditionType;

  /**
   * Which document label this condition applies to.
   * If not set and multiple labels are linked, the condition applies to ALL labels.
   * If set, the condition only applies to files with this specific label.
   */
  labelId?: number;

  // === FILE_EXISTS ===
  /** If true, checks file EXISTS. If false, checks file DOES NOT exist */
  fileExists?: boolean;

  // === EXPIRATION_DATE ===
  /** How to compare the expiration date */
  expirationComparison?: ExpirationComparison;
  /** Number of days for the comparison (used with more_than_days, within_days) */
  expirationDays?: number;

  // === FIELD_VALUE ===
  /** Field name on FileLog (e.g., 'validity', 'status', 'conformity') */
  field?: string;
  /** Comparison operator */
  operator?: '==' | '!=' | '>' | '<' | '>=' | '<=' | 'contains' | 'not_empty' | 'is_empty';
  /** Expected value */
  value?: string | number | boolean;

  // === AI_QUESTION ===
  /** Question to ask AI about the document */
  aiQuestion?: string;
  /** Expected answer from AI */
  expectedAnswer?: 'yes' | 'no';

  // === AI_REFERENCE ===
  /** Reference/value to look for in the document */
  aiReference?: string;
  /** What value is expected to be found */
  aiExpectedValue?: string;

  // === Shared AI hints ===
  /** Location hints for AI - where to look in the document */
  aiHints?: AILocationHints;

  // === DATABASE_COMPARISON ===
  /**
   * Database field to compare against (e.g., 'building.unitsCount', 'unit.netSurface')
   * The value is extracted from the building/unit entity at evaluation time.
   */
  databaseField?: string;
  /**
   * What value to extract from the document for comparison.
   * - 'extractionQuestion': Use value from a previous EXTRACTION question
   * - 'aiExtraction': Use AI to extract value directly from document
   */
  documentValueSource?: 'extractionQuestion' | 'aiExtraction';
  /**
   * If documentValueSource is 'extractionQuestion', the fieldName of the extraction question to use.
   * The extraction question must have a lower displayOrder (processed earlier).
   */
  extractionQuestionFieldName?: string;
  /**
   * If documentValueSource is 'aiExtraction', this is the prompt to use
   */
  documentExtractionPrompt?: string;
  /**
   * Comparison operator for database comparison
   */
  dbComparisonOperator?: '==' | '!=' | '>' | '<' | '>=' | '<=';
  /**
   * Expected result: whether values should match or mismatch for condition to pass
   */
  dbComparisonExpected?: 'match' | 'mismatch' | 'document_value_exists';

  // === QUESTION_ANSWER ===
  /**
   * The ID of another question whose answer to check.
   * This allows building dependencies between questions.
   */
  referencedQuestionId?: number;
  /**
   * The expected selected option ID for a single comparison.
   * Used when questionAnswerComparison is 'equals' or 'not_equals'.
   */
  expectedOptionId?: number;
  /**
   * The expected option value (fallback for unsaved options).
   * Backend resolves this to expectedOptionId when saving.
   */
  expectedOptionValue?: string;
  /**
   * Multiple allowed option IDs for OR logic.
   * Used when questionAnswerComparison is 'any_of'.
   */
  expectedOptionIds?: number[];
  /**
   * How to compare the referenced question's answer.
   * - 'equals': Selected option must match expectedOptionId
   * - 'not_equals': Selected option must NOT match expectedOptionId
   * - 'any_of': Selected option must be one of expectedOptionIds
   */
  questionAnswerComparison?: 'equals' | 'not_equals' | 'any_of';
}

/**
 * Evaluation result for a single condition (for debugging/display)
 */
export interface ConditionEvaluationResult {
  conditionId: string;
  conditionType: AnswerOptionConditionType;
  passed: boolean;
  reason?: string;
  foundValue?: string;
}

// ============================================
// Core Entities
// ============================================

export interface DueDiligenceAnswerOption {
  id: number;
  questionId: number;
  value: string;
  label: string;
  statusColor: DueDiligenceStatusColor;
  tooltipLabel?: string;
  isDefault: boolean;
  displayOrder: number;
  active: boolean;
  /** Root condition group for automatic selection (nested AND/OR structure) */
  conditionRoot?: ConditionGroup | null;
  /** @deprecated Use conditionRoot instead - flat conditions list without nesting */
  conditions?: AnswerOptionCondition[];
  /** @deprecated Use conditionRoot.logic instead */
  conditionLogic?: 'AND' | 'OR';
  createdAt: string;
  updatedAt: string;
}

export interface CumulativeConfig {
  approvalStatuses?: string[];
  enableTimeline?: boolean;
  trackAllDocuments?: boolean;
  enableSynthesis?: boolean;
}

export interface FileLabel {
  id: number;
  name: string;
  label?: string; // The actual label string from backend
  description?: string;
  documentBehavior?: 'replacing' | 'cumulative';
  cumulativeConfig?: CumulativeConfig | null;
}

export interface DueDiligenceQuestion {
  id: number;
  categoryId: number;
  fieldName: string;
  displayName: string;
  helpText?: string;
  displayOrder: number;
  active: boolean;
  // Document label links - which file labels can answer this question
  documentLabels?: FileLabel[];
  /** @deprecated Use documentLabels instead */
  documentLabelId?: number;
  // AI Extraction hint for this specific question
  aiExtractionHint?: string;
  answerType: DueDiligenceAnswerType;

  // EXTRACTION question fields (for answerType = EXTRACTION)
  /** Type of value to extract from document */
  extractionValueType?: ExtractionValueType | null;
  /** AI prompt to extract the value from document */
  extractionPrompt?: string | null;
  /** Human-readable label for the extracted value */
  extractionLabel?: string | null;

  // Automatic evaluation fields
  evaluationMode: DueDiligenceEvaluationMode;
  statusEvaluationConfig?: StatusEvaluationConfig | null;
  // Aggregation configuration for tracked values and timelines
  aggregationConfig?: AggregationConfig | null;
  // Question dependency configuration for question chaining
  dependencyConfig?: QuestionDependencyConfig | null;

  // Document behavior configuration (moved from FileLabel to Question)
  /**
   * How documents linked to this question are interpreted:
   * - 'replacing': Only the most recent document (by documentDate) determines the answer
   * - 'cumulative': All documents with linked labels are analyzed together
   */
  documentBehavior?: 'replacing' | 'cumulative';
  /**
   * Configuration for cumulative behavior (only used when documentBehavior === 'cumulative')
   */
  cumulativeConfig?: CumulativeConfig | null;

  // Timestamps
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  answerOptions: DueDiligenceAnswerOption[];
}

export interface DueDiligenceCategory {
  id: number;
  name: string;
  description?: string;
  icon?: string;
  scope: DueDiligenceScope;
  displayOrder: number;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  questions: DueDiligenceQuestion[];
}

/**
 * Basic source file info for audit trail display
 */
export interface SourceFileLogInfo {
  id: number;
  label?: string;
  sharePointFileUrl?: string;
  documentDate?: string;
  expirationDate?: string;
  fileName?: string;
}

export interface DueDiligenceResponse {
  id: number;
  buildingId?: string;
  unitId?: string;
  questionId: number;
  selectedOptionId?: number | null;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  updatedBy: string;
  createdBy?: string;
  question?: DueDiligenceQuestion;
  selectedOption?: DueDiligenceAnswerOption;

  // Answer fields for non-OPTIONS type questions (EXTRACTION, FREE_TEXT, NUMBER, DATE, BOOLEAN)
  /** For DATE/EXTRACTION(date) answer type */
  dateAnswer?: string | null;
  /** For NUMBER/EXTRACTION(number) answer type */
  numberAnswer?: number | null;
  /** For BOOLEAN/EXTRACTION(boolean) answer type */
  booleanAnswer?: boolean | null;
  /** For FREE_TEXT/EXTRACTION(string) answer type */
  freeTextAnswer?: string | null;

  // Audit trail fields
  /** How the stored response was entered */
  sourceType?: DueDiligenceResponseSourceType;
  /** AI confidence score (0-1) if AI extracted */
  aiConfidence?: number;
  /** ID of source file if AI extracted */
  sourceFileLogId?: number;
  /** Source file log with URL for document linking */
  sourceFileLog?: SourceFileLogInfo;
  /** Multiple source files for cumulative questions */
  sourceFileLogs?: SourceFileLogInfo[];
  /** Document date from source file for chronological awareness */
  sourceDocumentDate?: string | null;
  /** Whether the response has been validated */
  isValidated?: boolean;
  /** Who validated the response (clerk user id) */
  validatedBy?: string;
  /** When the response was validated */
  validatedAt?: string;
  /** Full condition evaluation results with AI reasoning */
  conditionEvaluations?: ConditionEvaluationsAudit;
}

// ============================================
// DTOs - Create
// ============================================

export interface CreateDueDiligenceCategoryDto {
  name: string;
  description?: string;
  icon?: string;
  scope: DueDiligenceScope;
  displayOrder?: number;
}

export interface CreateDueDiligenceQuestionDto {
  fieldName: string;
  displayName: string;
  helpText?: string;
  displayOrder?: number;
  answerType?: DueDiligenceAnswerType;
  // Document label links - which file labels can answer this question
  documentLabelIds?: number[];
  /** @deprecated Use documentLabelIds instead */
  documentLabelId?: number;
  aiExtractionHint?: string;
  // EXTRACTION question fields
  extractionValueType?: ExtractionValueType;
  extractionPrompt?: string;
  extractionLabel?: string;
  // Automatic evaluation fields
  evaluationMode?: DueDiligenceEvaluationMode;
  statusEvaluationConfig?: StatusEvaluationConfig;
  // Aggregation configuration
  aggregationConfig?: AggregationConfig;
  // Question dependency configuration
  dependencyConfig?: QuestionDependencyConfig;
}

export interface CreateDueDiligenceAnswerOptionDto {
  value: string;
  label: string;
  statusColor: DueDiligenceStatusColor;
  tooltipLabel?: string;
  isDefault?: boolean;
  displayOrder?: number;
  /** Root condition group for automatic selection (nested AND/OR structure) */
  conditionRoot?: ConditionGroup | null;
  /** @deprecated Use conditionRoot instead */
  conditions?: AnswerOptionCondition[];
  /** @deprecated Use conditionRoot.logic instead */
  conditionLogic?: 'AND' | 'OR';
}

// ============================================
// DTOs - Update
// ============================================

export interface UpdateDueDiligenceCategoryDto {
  name?: string;
  description?: string;
  icon?: string;
  scope?: DueDiligenceScope;
  displayOrder?: number;
  active?: boolean;
}

export interface UpdateDueDiligenceQuestionDto {
  fieldName?: string;
  displayName?: string;
  helpText?: string;
  displayOrder?: number;
  active?: boolean;
  answerType?: DueDiligenceAnswerType;
  // Document label links - which file labels can answer this question
  documentLabelIds?: number[];
  /** @deprecated Use documentLabelIds instead */
  documentLabelId?: number;
  aiExtractionHint?: string;
  // EXTRACTION question fields
  extractionValueType?: ExtractionValueType | null;
  extractionPrompt?: string | null;
  extractionLabel?: string | null;
  // Automatic evaluation fields
  evaluationMode?: DueDiligenceEvaluationMode;
  statusEvaluationConfig?: StatusEvaluationConfig | null;
  // Aggregation configuration
  aggregationConfig?: AggregationConfig | null;
  // Document behavior configuration
  documentBehavior?: 'replacing' | 'cumulative';
  cumulativeConfig?: {
    approvalStatuses?: string[];
    trackAllDocuments?: boolean;
    enableSynthesis?: boolean;
  } | null;
  // Question dependency configuration
  dependencyConfig?: QuestionDependencyConfig | null;
}

export interface UpdateDueDiligenceAnswerOptionDto {
  value?: string;
  label?: string;
  statusColor?: DueDiligenceStatusColor;
  tooltipLabel?: string;
  isDefault?: boolean;
  displayOrder?: number;
  active?: boolean;
  /** Root condition group for automatic selection (nested AND/OR structure) */
  conditionRoot?: ConditionGroup | null;
  /** @deprecated Use conditionRoot instead */
  conditions?: AnswerOptionCondition[];
  /** @deprecated Use conditionRoot.logic instead */
  conditionLogic?: 'AND' | 'OR';
}

// ============================================
// DTOs - Responses
// ============================================

export interface UpsertDueDiligenceResponseDto {
  buildingId?: string;
  unitId?: string;
  questionId: number;
  selectedOptionId?: number;
  notes?: string;
  // Answer fields for non-OPTIONS type questions (EXTRACTION, FREE_TEXT, NUMBER, DATE, BOOLEAN)
  dateAnswer?: string;
  numberAnswer?: number;
  booleanAnswer?: boolean;
  freeTextAnswer?: string;
}

export interface UpsertBulkDueDiligenceResponsesDto {
  responses: Array<{
    questionId: number;
    selectedOptionId?: number;
    notes?: string;
    buildingId?: string;
    unitId?: string;
    // Answer fields for non-OPTIONS type questions (EXTRACTION, FREE_TEXT, NUMBER, DATE, BOOLEAN)
    dateAnswer?: string;
    numberAnswer?: number;
    booleanAnswer?: boolean;
    freeTextAnswer?: string;
  }>;
}

// ============================================
// Dashboard / Dot Values
// ============================================

export interface QuestionDotValue {
  questionId: number;
  fieldName: string;
  displayName: string;
  statusColor: DueDiligenceStatusColor;
  tooltipLabel: string;
  hasResponse: boolean;
  /** How the question evaluates: 'manual' = needs user input, 'file_based' = automatic from file analysis */
  evaluationMode?: 'manual' | 'file_based';
  /** When file_based, the ID of the FileLog that determined the answer */
  sourceFileLogId?: number;
  /** When file_based, the display name of the source file */
  sourceFileName?: string;
  /** When file_based, the label of the source file */
  sourceFileLabel?: string;
  /** SharePoint URL for the source file */
  sourceFileSharePointUrl?: string;

  // Audit trail fields (for stored responses)
  /** How the stored response was originally entered */
  responseSourceType?: DueDiligenceResponseSourceType;
  /** Who created the response */
  createdBy?: string;
  /** When the response was created */
  createdAt?: string;
  /** Who last updated the response */
  updatedBy?: string;
  /** When the response was last updated */
  updatedAt?: string;
  /** AI confidence score (0-1) if AI extracted */
  aiConfidence?: number;
  /** Who validated the AI response (clerk user id) */
  validatedBy?: string;
  /** When the response was validated */
  validatedAt?: string;
  /** Full condition evaluation results with AI reasoning (for AI extracted responses) */
  conditionEvaluations?: ConditionEvaluationsAudit;
}

/**
 * Stored condition evaluation results for audit trail
 */
export interface ConditionEvaluationsAudit {
  /** Evaluation results for each answer option */
  optionEvaluations?: AnswerOptionEvaluationAudit[];
  /** Summary of the evaluation */
  summary?: string;
}

/**
 * Single answer option evaluation result
 */
export interface AnswerOptionEvaluationAudit {
  optionId: number;
  optionLabel: string;
  matched: boolean;
  conditionResults: SingleConditionResultAudit[];
  summary?: string;
}

/**
 * Single condition evaluation result
 */
export interface SingleConditionResultAudit {
  conditionId: string;
  conditionType: string;
  description: string;
  passed: boolean;
  details: {
    foundValue?: string;
    expectedValue?: string;
    comparison?: string;
    message?: string;
  };
  children?: SingleConditionResultAudit[];
  /** AI details for AI_QUESTION and AI_REFERENCE conditions */
  aiDetails?: {
    promptUsed: string;
    rawResponse: string;
    model: string;
    temperature?: number;
    interpretedAs?: string;
  };
}

export interface CategoryDotValues {
  categoryId: number;
  categoryName: string;
  categoryIcon?: string;
  scope: DueDiligenceScope;
  questions: QuestionDotValue[];
}

// ============================================
// Aggregation Types - Simplified & Intuitive
// ============================================
//
// Documents fall into two behavioral categories:
//
// 1. REPLACING DOCUMENTS (EPC, Keuringsattest, Insurance)
//    - New document = new truth, replaces previous
//    - Just use latest document's extracted values
//
// 2. CUMULATIVE DOCUMENTS (Vergunningen, Decisions)
//    - Documents build upon each other
//    - Can track specific values (e.g., "aantal vergunde wooneenheden")
//    - Can compare tracked values to database fields
//    - Can show timeline/history

/**
 * Document behavior type
 */
export type DocumentBehavior = 'replacing' | 'cumulative';

/**
 * Value type for tracked fields
 */
export type TrackedValueType = 'number' | 'string' | 'date' | 'boolean';

/**
 * Configuration for tracking a specific value from documents
 */
export interface TrackedValueConfig {
  enabled: boolean;
  /** Human-readable label for the tracked value */
  label: string;
  /** Type of value to extract */
  valueType: TrackedValueType;
  /** AI prompt to extract the value */
  extractionPrompt: string;
  /** Only update when document has approved status */
  onlyIfApproved?: boolean;
  /** What statuses count as "approved" */
  approvalStatuses?: string[];
}

/**
 * Configuration for comparing tracked value to database
 */
export interface DatabaseComparisonConfig {
  enabled: boolean;
  /** Database field path (e.g., 'building.units.length') */
  databaseField: string;
  /** Comparison operator */
  operator: '==' | '!=' | '>' | '<' | '>=' | '<=';
  /** Map results to answer option values */
  resultMapping: {
    onMatch: string;
    onMismatch: string;
    onNoValue: string;
  };
}

/**
 * Timeline configuration (for cumulative documents)
 */
export interface TimelineConfig {
  enabled: boolean;
  /** Generate AI synthesis of history */
  synthesisEnabled?: boolean;
}

/**
 * Main aggregation configuration for a DD question
 */
export interface AggregationConfig {
  enabled: boolean;
  /** How documents behave: replacing or cumulative */
  documentBehavior: DocumentBehavior;
  /** Track a specific value from documents */
  trackedValue?: TrackedValueConfig;
  /** Compare tracked value to database field */
  databaseComparison?: DatabaseComparisonConfig;
  /** Show timeline (only for cumulative) */
  timeline?: TimelineConfig;
}

/**
 * State of a tracked value
 */
export interface TrackedValueState {
  value: string | number | boolean | null;
  valueType: TrackedValueType;
  sourceFileLogId: number;
  sourceFileName: string;
  documentDate: string;
  updatedAt: string;
  reasoning?: string;
  confidence?: number;
}

/**
 * Timeline entry for a document
 */
export interface TimelineEntry {
  id: string;
  fileLogId: number;
  fileName: string;
  documentDate: string;
  summary: string;
  status?: string;
  addedAt: string;
}

/**
 * Result of comparing tracked value with database
 */
export interface ComparisonResult {
  matches: boolean;
  trackedValue: string | number | boolean | null;
  databaseValue: string | number | boolean | null;
  databaseField: string;
  message: string;
  comparedAt: string;
}

/**
 * Full aggregation state for a question
 */
export interface DueDiligenceQuestionState {
  id: number;
  questionId: number;
  buildingId: string;
  unitId?: string;
  trackedValue?: TrackedValueState;
  timeline?: TimelineEntry[];
  synthesis?: string;
  comparison?: ComparisonResult;
  documentsProcessed: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * Available database field for comparison
 */
export interface DatabaseField {
  key: string;
  label: string;
  type: TrackedValueType;
  description: string;
}

/**
 * Category of database fields
 */
export interface DatabaseFieldCategory {
  category: string;
  fields: DatabaseField[];
}

// ============================================
// UI Helpers
// ============================================

/**
 * Maps status color to Tailwind CSS classes for the dot indicator
 */
export const statusColorToClasses: Record<DueDiligenceStatusColor, string> = {
  [DueDiligenceStatusColor.GREEN]: 'bg-green-500',
  [DueDiligenceStatusColor.ORANGE]: 'bg-orange-500',
  [DueDiligenceStatusColor.RED]: 'bg-red-500',
  [DueDiligenceStatusColor.TRANSPARENT]: 'bg-transparent border border-gray-300'
};

/**
 * Maps status color to human-readable Dutch labels
 */
export const statusColorLabels: Record<DueDiligenceStatusColor, string> = {
  [DueDiligenceStatusColor.GREEN]: 'In orde',
  [DueDiligenceStatusColor.ORANGE]: 'Aandacht nodig',
  [DueDiligenceStatusColor.RED]: 'Actie vereist',
  [DueDiligenceStatusColor.TRANSPARENT]: 'Niet van toepassing'
};

/**
 * Maps response source type to human-readable Dutch labels for audit trail
 */
export const responseSourceTypeLabels: Record<DueDiligenceResponseSourceType, string> = {
  [DueDiligenceResponseSourceType.MANUAL]: 'Handmatig ingevoerd',
  [DueDiligenceResponseSourceType.AI_EXTRACTED]: 'AI geëxtraheerd (dropzone)',
  [DueDiligenceResponseSourceType.AI_VALIDATED]: 'AI geëxtraheerd, gevalideerd',
  [DueDiligenceResponseSourceType.AI_EDITED]: 'AI geëxtraheerd, bewerkt',
  [DueDiligenceResponseSourceType.SYSTEM_REEVALUATION]: 'Automatisch geherwaardeerd',
};

// ============================================
// Question Dependency Configuration
// ============================================
//
// Enables question chaining: questions can reference results from previous questions.
//
// Example flow:
// Q1: "Hoeveel wooneenheden zijn vergund?" (EXTRACTION type)
//     → Extracts: 5
//     → NO status dot displayed
//
// Q2: "Klopt het aantal vergunde wooneenheden?" (OPTIONS type)
//     dependencyConfig.sourceQuestionFieldName: "aantal_vergunde_wooneenheden"
//     → References Q1's extracted value (5)
//     → Compares to building.numUnitsWithPermit (5)
//     → Match: Green dot "Ja, aantal klopt"

/**
 * How to use the source question's result
 */
export type DependencyUseAs = 'comparison_value' | 'condition_input';

/**
 * Comparison operator for database comparison
 */
export type DependencyComparisonOperator = '==' | '!=' | '>' | '<' | '>=' | '<=';

/**
 * Result mapping - which answer option to select based on comparison result
 */
export interface DependencyResultMapping {
  /** Answer option value to select when values match */
  onMatch: string;
  /** Answer option value to select when values mismatch */
  onMismatch: string;
  /** Answer option value to select when source question has no value */
  onNoValue: string;
}

/**
 * Comparison configuration for comparing source question result to database field
 */
export interface DependencyComparison {
  /** Database field to compare against (e.g., 'building.numUnitsWithPermit', 'unit.netSurface') */
  databaseField: string;
  /** Comparison operator */
  operator: DependencyComparisonOperator;
  /** Result mapping - which answer option to select based on comparison result */
  resultMapping: DependencyResultMapping;
}

/**
 * Question Dependency Configuration
 *
 * Allows a question to reference results from a previous question (by fieldName).
 * The source question must have a lower displayOrder (processed first).
 */
export interface QuestionDependencyConfig {
  /**
   * Which question's result to use (by fieldName).
   * The source question must be in the same category with a lower displayOrder.
   * Example: "aantal_vergunde_wooneenheden"
   */
  sourceQuestionFieldName: string;

  /**
   * How to use the source value:
   * - 'comparison_value': Compare source question result to database field
   * - 'condition_input': Use as input for condition evaluation (future)
   */
  useAs: DependencyUseAs;

  /**
   * Comparison configuration when useAs is 'comparison_value'.
   * Compares the source question's extracted value to a database field.
   */
  comparison?: DependencyComparison;
}

// ============================================
// Response History / Audit Trail
// ============================================

/**
 * Change type for history records - describes what kind of change was made
 */
export type ResponseChangeType =
  | 'created'
  | 'updated'
  | 'validated'
  | 'ai_extracted'
  | 'ai_validated'
  | 'ai_edited';

/**
 * Human-readable labels for change types
 */
export const responseChangeTypeLabels: Record<ResponseChangeType, string> = {
  created: 'Aangemaakt',
  updated: 'Bijgewerkt',
  validated: 'Gevalideerd',
  ai_extracted: 'AI geëxtraheerd',
  ai_validated: 'AI gevalideerd',
  ai_edited: 'AI bewerkt',
};

/**
 * A single history record representing a change to a response
 */
export interface DueDiligenceResponseHistory {
  id: number;
  responseId: number;
  questionId: number;
  buildingId?: string;
  unitId?: string;

  // The answer at this point in time
  selectedOptionId?: number;
  selectedOption?: DueDiligenceAnswerOption;

  // Extraction answer fields (for EXTRACTION type questions)
  freeTextAnswer?: string;
  dateAnswer?: string;
  numberAnswer?: number;
  booleanAnswer?: boolean;

  notes?: string;

  // Source and audit information
  sourceType: DueDiligenceResponseSourceType;
  aiConfidence?: number;
  sourceFileLogId?: number;
  sourceFileLog?: SourceFileLogInfo;
  /** Multiple source file IDs for cumulative questions (snapshot at time of change) */
  sourceFileLogIds?: number[];

  // Who made this change
  changedBy?: string;

  // Type of change
  changeType: ResponseChangeType;

  // Description of what changed
  changeDescription?: string;

  // Validation info at this point
  isValidated: boolean;
  validatedBy?: string;
  validatedAt?: string;

  // When this history record was created
  createdAt: string;
}
